// src/pages/markets/board/api/useBoardTick.ts

import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

export const TICK_OFFSET_MS = 35_000;
export const EVENT_REFRESH_TICKS = 5;

// Aligned to the wall clock, not to mount time: scores are written around :30s,
// so a mount-relative interval reads data that is on average half a minute old.
export function useWallClockTick(onTick: () => void, offsetMs: number = TICK_OFFSET_MS): void {
    const cb = useRef(onTick);
    cb.current = onTick;

    useEffect(() => {
        let timer: ReturnType<typeof setTimeout>;

        const schedule = () => {
            const now = Date.now();
            const next = Math.ceil((now - offsetMs) / 60_000) * 60_000 + offsetMs;
            timer = setTimeout(() => {
                cb.current();
                schedule();
            }, Math.max(next - now, 500));
        };

        schedule();
        return () => clearTimeout(timer);
    }, [offsetMs]);
}

export function useBoardRefresh(enabled = true): void {
    const queryClient = useQueryClient();
    const tickCount = useRef(0);

    useWallClockTick(() => {
        if (!enabled) return;
        if (typeof document !== 'undefined' && document.hidden) return;
        tickCount.current += 1;
        void queryClient.invalidateQueries({ queryKey: ['board', 'rank'] });
        void queryClient.invalidateQueries({ queryKey: ['board', 'score'] });
        if (tickCount.current % EVENT_REFRESH_TICKS === 0) {
            void queryClient.invalidateQueries({ queryKey: ['board', 'event'] });
        }
    });
}